import type { FC } from 'react'
import type { HistoryItem, SceneProps } from '@/components/landing/games/scene-ui'

import { useEffect, useRef, useState } from 'react'
import { Check, Rocket } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMultiplier } from '@/lib/format'
import { useElementSize } from '@/hooks/use-element-size'
import { HistoryChip, SceneBadge, SceneLabel } from '@/components/landing/games/scene-ui'

type Phase = 'rising' | 'crashed' | 'waiting'

type Player = {
    name: string
    currency: string
    target: number
}

const GROWTH = 0.3
const EMBER = '#FF5B1F'
const DANGER = '#FF3D5E'
const PAD = { left: 46, right: 18, top: 20, bottom: 30 }
const SAMPLES = 72

const PLAYERS: Player[] = [
    { name: 'player_48213', currency: 'USDT', target: 1.5 },
    { name: 'player_10027', currency: 'SOL', target: 2 },
    { name: 'player_77310', currency: 'USDC', target: 3.25 },
    { name: 'player_52044', currency: 'USDT', target: 10 }
]

const POSTER_CRASH = 2.64

const POSTER_HISTORY: HistoryItem[] = [
    { id: 6, value: 1.42 },
    { id: 5, value: 6.2 },
    { id: 4, value: 1.05 },
    { id: 3, value: 2.9 },
    { id: 2, value: 1.88 }
]

const multiplierAt = (time: number): number => Math.exp(GROWTH * time)
const timeFor = (value: number): number => Math.log(value) / GROWTH

const sampleCrash = (): number => {
    const raw = 0.99 / (1 - Math.random())
    return Math.min(60, Math.max(1, Math.floor(raw * 100) / 100))
}

const niceStep = (span: number, steps: number[], maxTicks: number): number => {
    return steps.find(step => span / step <= maxTicks) ?? steps[steps.length - 1]
}

const CrashScene: FC<SceneProps> = ({ active }) => {
    const chartRef = useRef<HTMLDivElement>(null)
    const { width, height } = useElementSize(chartRef)
    const [time, setTime] = useState(timeFor(POSTER_CRASH) * 0.999)
    const [crashAt, setCrashAt] = useState(POSTER_CRASH)
    const [phase, setPhase] = useState<Phase>('rising')
    const [history, setHistory] = useState<HistoryItem[]>(POSTER_HISTORY)
    const nextId = useRef(7)

    useEffect(() => {
        if (!active) return

        let frame = 0
        let timer = 0
        let alive = true

        const startRound = () => {
            if (!alive) return

            const crash = sampleCrash()
            const duration = timeFor(crash)
            const began = performance.now()

            setCrashAt(crash)
            setPhase('rising')
            setTime(0)

            const tick = (now: number) => {
                if (!alive) return

                const elapsed = (now - began) / 1000

                if (elapsed >= duration) {
                    setTime(duration)
                    setPhase('crashed')

                    const id = nextId.current
                    nextId.current += 1
                    setHistory(previous => [{ id, value: crash }, ...previous].slice(0, 6))

                    timer = window.setTimeout(() => {
                        setPhase('waiting')
                        timer = window.setTimeout(startRound, 1100)
                    }, 1700)
                    return
                }

                setTime(elapsed)
                frame = requestAnimationFrame(tick)
            }

            frame = requestAnimationFrame(tick)
        }

        timer = window.setTimeout(startRound, 700)

        return () => {
            alive = false
            cancelAnimationFrame(frame)
            window.clearTimeout(timer)
        }
    }, [active])

    const current = Math.min(multiplierAt(time), crashAt)
    const rising = phase === 'rising'
    const stroke = rising ? EMBER : DANGER

    const tMax = Math.max(6, time * 1.15)
    const mMax = Math.max(2, current * 1.2)
    const plotWidth = Math.max(0, width - PAD.left - PAD.right)
    const plotHeight = Math.max(0, height - PAD.top - PAD.bottom)
    const floor = PAD.top + plotHeight
    const x = (value: number): number => PAD.left + (value / tMax) * plotWidth
    const y = (value: number): number => floor - ((value - 1) / (mMax - 1)) * plotHeight

    const points: string[] = []

    for (let i = 0; i <= SAMPLES; i++) {
        const t = (time * i) / SAMPLES
        points.push(`${x(t).toFixed(1)},${y(Math.min(multiplierAt(t), crashAt)).toFixed(1)}`)
    }

    const line = `M ${points.join(' L ')}`
    const area = `${line} L ${x(time).toFixed(1)},${floor.toFixed(1)} L ${PAD.left},${floor.toFixed(1)} Z`

    const headX = x(time)
    const headY = y(current)
    const before = Math.max(0, time - Math.max(0.05, time * 0.03))
    const angle = (Math.atan2(headY - y(Math.min(multiplierAt(before), crashAt)), headX - x(before)) * 180) / Math.PI

    const mStep = niceStep(mMax - 1, [0.25, 0.5, 1, 2, 5, 10, 20], 4)
    const yTicks: number[] = []
    for (let value = 1; value <= mMax + 1e-9; value += mStep) yTicks.push(value)

    const tStep = niceStep(tMax, [1, 2, 5, 10, 20], 6)
    const xTicks: number[] = []
    for (let value = 0; value <= tMax + 1e-9; value += tStep) xTicks.push(value)

    return (
        <div className='absolute inset-0 flex flex-col px-4 pb-4 pt-12 @md:px-6 @md:pb-6'>
            <div ref={chartRef} className='relative min-h-0 flex-1'>
                {width > 0 && height > 0 && (
                    <svg width={width} height={height} aria-hidden='true' className='absolute inset-0'>
                        <defs>
                            <linearGradient id='crash-area' x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='0%' stopColor={stroke} stopOpacity='0.34' />
                                <stop offset='100%' stopColor={stroke} stopOpacity='0' />
                            </linearGradient>
                        </defs>

                        {yTicks.map(value => (
                            <g key={value}>
                                <line x1={PAD.left} x2={width - PAD.right} y1={y(value)} y2={y(value)} stroke='rgba(255, 255, 255, 0.06)' strokeDasharray='3 6' />
                                <text x={PAD.left - 10} y={y(value)} textAnchor='end' dominantBaseline='middle' fontSize='10' className='fill-steel font-mono'>
                                    {value.toFixed(value % 1 === 0 ? 1 : 2)}×
                                </text>
                            </g>
                        ))}

                        {xTicks.map(value => (
                            <text key={value} x={x(value)} y={floor + 20} textAnchor='middle' fontSize='10' className='fill-steel/70 font-mono'>
                                {value}s
                            </text>
                        ))}

                        <line x1={PAD.left} x2={width - PAD.right} y1={floor} y2={floor} stroke='rgba(255, 255, 255, 0.14)' />

                        {time > 0 && (
                            <g className={cn('transition-opacity duration-500', phase === 'waiting' && 'opacity-30')}>
                                <path d={area} fill='url(#crash-area)' />
                                <path d={line} fill='none' stroke={stroke} strokeOpacity='0.22' strokeWidth='10' strokeLinecap='round' strokeLinejoin='round' />
                                <path d={line} fill='none' stroke={stroke} strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
                            </g>
                        )}

                        {PLAYERS.filter(player => player.target <= current && player.target <= crashAt).map(player => (
                            <g key={player.name}>
                                <circle cx={x(timeFor(player.target))} cy={y(player.target)} r='5' fill='#08080A' stroke={EMBER} strokeWidth='2' />
                                <circle cx={x(timeFor(player.target))} cy={y(player.target)} r='1.8' fill={EMBER} />
                            </g>
                        ))}

                        {phase === 'crashed' && (
                            <g>
                                <circle cx={headX} cy={headY} r='7' fill={DANGER} />
                                <circle cx={headX} cy={headY} r='18' fill='none' stroke={DANGER} strokeWidth='2' className='origin-center animate-ring-out [transform-box:fill-box]' />
                            </g>
                        )}
                    </svg>
                )}

                {rising && width > 0 && time > 0.05 && (
                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute'
                        style={{ left: headX, top: headY, transform: `translate(-50%, -50%) rotate(${angle + 45}deg)` }}
                    >
                        <span className='absolute left-1/2 top-1/2 size-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ember/30 blur-md' />
                        <Rocket strokeWidth={1.6} className='relative size-6 fill-ink text-bone' />
                    </div>
                )}

                <div className='pointer-events-none absolute inset-x-0 top-[26%] flex flex-col items-center text-center @md:top-[44%]'>
                    <span
                        className={cn(
                            'font-mono text-5xl font-medium tabular-nums tracking-tight transition-colors duration-300 @lg:text-7xl',
                            rising ? 'text-bone' : phase === 'crashed' ? 'text-danger' : 'text-steel/60'
                        )}
                    >
                        {formatMultiplier(current)}×
                    </span>
                    <SceneLabel className='mt-3'>
                        {rising ? 'Climbing' : phase === 'crashed' ? 'Crashed' : 'Next round'}
                    </SceneLabel>
                </div>

                <div className='absolute left-12 top-1 hidden w-[232px] rounded-2xl border border-white/[0.06] bg-ink/70 p-3.5 backdrop-blur @md:block'>
                    <div className='flex items-center justify-between'>
                        <SceneLabel>Players</SceneLabel>
                        <SceneLabel>Cash out</SceneLabel>
                    </div>

                    <ul className='mt-2.5 grid gap-1.5'>
                        {PLAYERS.map(player => {
                            const cashed = player.target <= crashAt && (!rising || current >= player.target)
                            const lost = !rising && player.target > crashAt

                            return (
                                <li key={player.name} className='flex items-center justify-between gap-2 font-mono text-[10px]'>
                                    <span className='truncate text-steel'>
                                        {player.name}
                                        <span className='ml-1.5 text-steel/50'>{player.currency}</span>
                                    </span>
                                    <span className={cn('flex shrink-0 items-center gap-1 tabular-nums transition-colors duration-300', cashed ? 'text-ember' : lost ? 'text-danger/80 line-through' : 'text-bone/55')}>
                                        {cashed && <Check aria-hidden='true' className='size-3' />}
                                        ×{player.target.toFixed(2)}
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </div>

            <div className='mt-3 flex items-center justify-between gap-3'>
                <div className='flex h-5 min-w-0 gap-1 overflow-hidden'>
                    {history.map(item => (
                        <HistoryChip key={item.id} hot={item.value >= 2}>
                            {formatMultiplier(item.value)}×
                        </HistoryChip>
                    ))}
                </div>

                <SceneBadge tone={rising ? 'ember' : phase === 'crashed' ? 'danger' : 'muted'} className='shrink-0'>
                    {rising ? 'Round live' : phase === 'crashed' ? `Bust at ${formatMultiplier(crashAt)}×` : 'Next round'}
                </SceneBadge>
            </div>
        </div>
    )
}

export default CrashScene
